import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useLocation, Link } from 'react-router-dom';

export const CoachManage = () => {
  const { staff } = useStaffAuthContext();

  const [coachData, setCoachData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [accountType, setAccountType] = useState("");

  const fetchCoachData = async (page) => {
    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(
        `${API_URL.GET_COACH_ALL}?page=${page}&limit=50`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        }
      );
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to fetch data");
      }

      setCoachData(data.coaches);
      setFilteredData(data.coaches);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  const deleteCoach = async (id) => {
    try {
      setIsDeleting(true);
      const response = await fetch(`${API_URL.DELETE_COACH}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${staff.token}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to delete coach");
      }

      fetchCoachData(currentPage);
      alert("Coach deleted successfully!");
    } catch (err) {
      alert(`Error deleting coach: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this coach?")) {
      deleteCoach(id);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    filterData(query, accountType);
  };

  const handleAccountTypeChange = (event) => {
    setAccountType(event.target.value);
    filterData(searchQuery, event.target.value);
  };

  const filterData = (query, accountType) => {
    let filtered = coachData;

    if (query.trim() !== "") {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(
        (coach) =>
          coach.firstname.toLowerCase().includes(lowerQuery) ||
          coach.lastname.toLowerCase().includes(lowerQuery) ||
          coach.email.toLowerCase().includes(lowerQuery) ||
          coach.city.toLowerCase().includes(lowerQuery) ||
          coach.gender.toLowerCase().includes(lowerQuery)
      );
    }

    if (accountType) {
      filtered = filtered.filter((coach) => coach.account_type === accountType);
    }

    setFilteredData(filtered);
  };

  useEffect(() => {
    fetchCoachData(currentPage);
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">
        <h1 className="text-2xl font-bold mb-4">Manage Coaches</h1>

        <div className="flex justify-end gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by name, email, city, or gender"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-1/4 p-2 border border-gray-300 rounded"
          />

          <select
            value={accountType}
            onChange={handleAccountTypeChange}
            className="p-2 border border-gray-300 rounded"
          >
            <option value="">All Account Types</option>
            <option value="FREE PLAN">FREE PLAN</option>
            <option value="STATIC PLAN">STATIC PLAN</option>
          </select>
        </div>

        <div className="flex-1 overflow-auto border rounded shadow bg-white">
          <table className="table-auto w-full">
            <thead className="sticky top-0 bg-gray-200">
              <tr>
              <th className="px-4 py-2 text-left">Name & Email</th>
                <th className="px-4 py-2 text-left">City</th>
                <th className="px-4 py-2 text-left">Gender</th>
                <th className="px-4 py-2 text-left">Account Type</th>
                <th className="px-4 py-2 text-left">Runrocket/LoadVelocity</th> {/* New Column */}
                <th className="px-4 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <p className="text-blue-600">Loading data...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <p className="text-red-600">{error}</p>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    <p>No records found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((coach) => (
                  <tr
                    key={coach._id}
                    className="hover:bg-gray-100 border-b border-gray-200 bg-gray-50 even:bg-white"
                  >
                    <td className="px-4 py-2">
                      <div>
                        <p className="font-semibold">
                          {coach.firstname} {coach.lastname}
                        </p>
                        <p className="text-sm text-gray-600">{coach.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-2">{coach.city}</td>
                    <td className="px-4 py-2">{coach.gender}</td>
                    <td className="px-4 py-2">{coach.account_type}</td>

                    <td className="px-4 py-2">
                        <div className="flex flex-col gap-2">
                            {/* Conditionally render Test Results and Transactions for coaches who are not on the "FREE PLAN" */}
                            {coach.account_type !== "FREE PLAN" && (
                              <Link
                                to={`/administrator/coach/runrocket/test-results/${coach._id}`}
                                className="px-3 py-1 text-xs bg-blue-500 text-white text-center rounded hover:bg-blue-600"
                              >
                                RR Test Results
                              </Link>
                            )}
                          
                              {coach.account_type !== "FREE PLAN" && (
                              <Link
                                to={`/administrator/coach/loadvelocity/test-results/${coach._id}`}
                                className="px-3 py-1 text-xs bg-blue-500 text-white text-center rounded hover:bg-blue-600"
                              >
                                LV Test Results
                              </Link>
                            )}
                          </div>
                    </td>  

                    <td className="px-4 py-2">
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleDelete(coach._id)}
                        className="px-2 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                        disabled={isDeleting}
                      >
                        Delete
                      </button>

                      <Link
                            to={`/administrator/coach/edit/${coach._id}`}
                            className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-center"
                          >
                            Edit
                      </Link>

                      {/* Conditionally render Test Results and Transactions for coaches who are not on the "FREE PLAN" */}
                      {coach.account_type !== "FREE PLAN" && (
                          <Link
                            to={`/administrator/coach/payment-transactions/${coach._id}`}
                            className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-center"
                          >
                            Transactions
                          </Link>
                      )}      
                    </div>

                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded ${
              currentPage === 1
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Previous
          </button>

          <p className="text-sm">Page {currentPage} of {totalPages}</p>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded ${
              currentPage === totalPages
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};
